/**
 * Frontend service for the admin-actions Medplum Bot.
 *
 * Wraps `medplum.executeBot()` with typed inputs/outputs.
 * Mirrors the pattern in healthGorillaService.ts.
 *
 * The Bot must be registered in Medplum with:
 *   identifier: { system: 'https://unify.health/bots', value: 'admin-actions' }
 *   runtimeVersion: 'vmcontext'
 *
 * Execution target: set `VITE_MEDPLUM_ADMIN_ACTIONS_BOT_ID` to the Bot resource id from Medplum Admin
 * (Dashboard → Bot → ID) so `$execute` uses `Bot/{id}/$execute` instead of searching by identifier.
 */

import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { ContactPoint, HumanName, Identifier, Patient, Practitioner } from '@medplum/fhirtypes';
import type { User } from '../pages/users/types/rbac';

// ---------------------------------------------------------------------------
// Bot identifier (default — must match Medplum Admin → Bot → Identifier)
// ---------------------------------------------------------------------------

export const ADMIN_ACTIONS_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'admin-actions',
} as const;

/**
 * Resolves the first argument to `medplum.executeBot`.
 * Prefer `VITE_MEDPLUM_ADMIN_ACTIONS_BOT_ID` when identifier-based `$execute` fails or is ambiguous.
 */
export function getAdminActionsBotExecuteTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_ADMIN_ACTIONS_BOT_ID?.trim();
  if (id) {
    return id;
  }
  const system =
    import.meta.env.VITE_MEDPLUM_ADMIN_ACTIONS_BOT_IDENTIFIER_SYSTEM?.trim() ??
    ADMIN_ACTIONS_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_ADMIN_ACTIONS_BOT_IDENTIFIER_VALUE?.trim() ??
    ADMIN_ACTIONS_BOT_IDENTIFIER.value;
  return { system, value };
}

// ---------------------------------------------------------------------------
// Input types (match admin-actions.ts handler expectations)
// ---------------------------------------------------------------------------

export interface EditMembersBotInput {
  action: 'edit-members';
  userId: string;
  updates: {
    name?: string;
    email?: string;
    phone?: string;
    bio?: string;
    aliases?: string[];
    /** Serialized as JSON on Practitioner; may include nested `demographics`. */
    clinical?: Partial<NonNullable<User['clinical']>>;
    /** Practitioner.name nickname slots; applied with legal-name fill-in. */
    preferredNames?: { first?: string; last?: string };
    /** Practice site Location ids; persisted on `PractitionerRole.location` by the bot. */
    locationIds?: string[];
  };
}

export interface SetRoleBotInput {
  action: 'set-role';
  userId: string;
  role: string;
  additionalRoles?: string[];
  permissions?: string;
}

export interface SetPrimaryRoleBotInput {
  action: 'set-primary-role';
  userId: string;
  primaryRole: string;
}

export interface ResendInviteBotInput {
  action: 'resend-invite';
  email: string;
  projectId?: string;
  recaptchaToken?: string;
  recaptchaSiteKey?: string;
}

export interface InviteUserBotInput {
  action: 'invite-user';
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  resourceType?: 'Practitioner' | 'Patient' | 'RelatedPerson';
  orgId?: string;
  /** Optional legacy hint; practice-admin-actions resolves the canonical AccessPolicy by role. */
  accessPolicyId?: string;
  accessParameters?: { name: string; valueReference: { reference: string } }[];
  projectId?: string;
  uiPermissions?: string;
  additionalRoles?: string[];
}

export interface CreatePracticeBotInput {
  action: 'create-practice';
  projectId?: string;
  uiPermissions?: string;
  practice: {
    name: string;
    legalName?: string;
    contactEmail?: string;
    phone?: string;
    ein?: string;
    npi?: string;
    address?: {
      line?: string[];
      city?: string;
      state?: string;
      postalCode?: string;
      country?: string;
    };
  };
  owner: {
    firstName?: string;
    lastName?: string;
    name?: string;
    email: string;
    phone?: string;
  };
}

export interface CreatePracticeResult {
  organization: { id: string; reference: string; name: string };
  owner: { practitionerId: string; practitionerReference: string; email: string };
  invite?: { status: string; userId?: string; membershipId?: string };
  roleAssignment?: { status: string; practitionerRoleId?: string };
  warnings?: string[];
}

export interface ManageEocPartyBotInput {
  action: 'manage-eoc-party';
  operation:
  | 'add-participant-existing'
  | 'add-participant-new'
  | 'remove-participant'
  | 'create-contact-related-person';
  episodeId: string;
  primaryPatientId: string;
  orgId?: string;
  participantPatientId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  relationship?: string;
  phone?: string;
  contactName?: string;
  relatedPersonId?: string;
  projectId?: string;
}

export interface ManageEocPartyResult {
  episodeId: string;
  operation: ManageEocPartyBotInput['operation'];
  relatedPersonId?: string;
  patientId?: string;
}

export interface EnsurePracticeClientBotInput {
  action: 'ensure-practice-client';
  patientId: string;
  organizationId: string;
}

export interface CreateClientAndEpisodeBotInput {
  action: 'create-client-and-episode';
  idempotencyKey: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  zip?: string;
  language?: string;
  organizationId: string;
  practitionerId?: string;
  therapyTypeCode: string;
  projectId?: string;
}

export interface CreateClientAndEpisodeResult {
  patientId: string;
  episodeOfCareId: string;
  serviceRequestId: string;
}

export interface UpdateProviderSupervisorBotInput {
  action: 'update-provider-supervisor';
  providerId: string;
  supervisorId: string | null;
}

export interface UpdateRequiresCosignatureBotInput {
  action: 'update-requires-cosignature';
  providerId: string;
  requiresCosignature: boolean;
}

export interface UpdateSupervisorSuperviseesBotInput {
  action: 'update-supervisor-supervisees';
  supervisorId: string;
  superviseeIds: string[];
}

export interface RoleTransitionRequest {
  practitionerId: string;
  targetDefaultRole: string;
  targetFunctionalRoles: string[];
  treatsClients: boolean;
  supervisesPractitioners: boolean;
  /** `offboard` = Deactivate user closeout (empty functional roles, cancel-all appts). */
  mode?: 'role-change' | 'offboard';
  /**
   * Legacy offboard payload — still sent so older deployed admin-actions bots pass validation.
   * Current server ignores this (no practitioner-deactivation extension).
   */
  accountDeactivation?: { reasonCode: string; detail?: string };
  addSuperviseeIds?: string[];
  superviseeDispositions?: Array<{
    superviseeId: string;
    replacementSupervisorId: string | null;
    allowUnsupervised?: boolean;
    adminOwnerId?: string;
  }>;
  clientDispositions?: Array<{
    episodeOfCareId: string;
    patientId: string;
    kind: 'transfer' | 'discharge' | 'transfer-pending' | 'matching-pool';
    replacementProviderId?: string;
    adminOwnerId?: string;
    transferExplanation?: string;
  }>;
  appointmentDispositions?: Array<{
    appointmentId: string;
    kind: 'transfer' | 'reschedule' | 'cancel' | 'preserve';
    replacementProviderId?: string;
  }>;
  groupFacilitatorDispositions?: Array<{
    groupId: string;
    replacementFacilitatorId?: string;
    action?: 'replace' | 'remove';
  }>;
  emergencyOverride?: boolean;
  adminOwnerId?: string;
  operationId?: string;
  transitionTaskId?: string;
}

export interface RoleTransitionImpactReport {
  practitionerId: string;
  practitionerDisplay?: string;
  currentDefaultRole?: string;
  currentFunctionalRoles: string[];
  targetDefaultRole: string;
  targetFunctionalRoles: string[];
  treatsClients: boolean;
  supervisesPractitioners: boolean;
  supervisees: Array<{ id: string; display?: string; pendingCosignCount: number }>;
  ownSupervisorId?: string;
  activeClients: Array<{
    episodeOfCareId: string;
    patientId: string;
    patientDisplay?: string;
    status?: string;
    serviceType?: string;
    activeMedManagementProvider?: { id: string; display?: string };
  }>;
  futureAppointments: Array<{ id: string; start?: string; status?: string; patientId?: string }>;
  pendingDocumentation: Array<{ id: string; kind: string }>;
  pendingCosignatures: Array<{ id: string; kind: string }>;
  openClinicalTasks: Array<{ id: string; kind: string }>;
  activeSchedules?: Array<{ id: string; active?: boolean }>;
  groupFacilitatorRoles?: Array<{
    groupId: string;
    groupDisplay?: string;
    role: 'primary-facilitator' | 'facilitator';
  }>;
  recordsRequiringManualResolution: string[];
  operationId: string;
}

export interface RoleTransitionExecuteResult {
  operationId: string;
  transitionTaskId: string;
  status: 'completed' | 'failed' | 'in-progress';
  steps: Array<{ step: string; status: string; detail?: string }>;
  warnings: string[];
  errors: Array<{ code: string; message: string }>;
  impact: RoleTransitionImpactReport;
}

export interface AnalyzeRoleTransitionBotInput {
  action: 'analyze-role-transition';
  request: RoleTransitionRequest;
}

export interface ExecuteRoleTransitionBotInput {
  action: 'execute-role-transition';
  request: RoleTransitionRequest;
}

export interface SwapPracticeOwnerBotInput {
  action: 'swap-practice-owner';
  orgId: string;
  outgoingOwnerId: string;
  incomingOwnerId: string;
  outgoingNewRole?: string;
  operationId?: string;
}

export interface SendSystemStatusTestEmailResult {
  recipientCount: number;
  queuedCount: number;
  failedCount: number;
}

export interface DeactivateUserBotInput {
  action: 'deactivate-user';
  userId: string;
}

export interface ReactivateUserBotInput {
  action: 'reactivate-user';
  userId: string;
  /** Omit after offboard — restores account access only; assign roles via Add role. */
  role?: string;
  permissions?: string;
  accessPolicyId?: string;
}

type AdminActionBotInput =
  | EditMembersBotInput
  | SetRoleBotInput
  | SetPrimaryRoleBotInput
  | ResendInviteBotInput
  | InviteUserBotInput
  | CreatePracticeBotInput
  | ManageEocPartyBotInput
  | EnsurePracticeClientBotInput
  | CreateClientAndEpisodeBotInput
  | UpdateProviderSupervisorBotInput
  | UpdateRequiresCosignatureBotInput
  | UpdateSupervisorSuperviseesBotInput
  | AnalyzeRoleTransitionBotInput
  | ExecuteRoleTransitionBotInput
  | SwapPracticeOwnerBotInput
  | DeactivateUserBotInput
  | ReactivateUserBotInput;

// ---------------------------------------------------------------------------
// Generic bot executor
// ---------------------------------------------------------------------------

async function executeAdminAction<T>(
  medplum: MedplumClient,
  input: AdminActionBotInput,
): Promise<T> {
  return medplum.executeBot(
    getAdminActionsBotExecuteTarget(),
    input,
    ContentType.JSON,
  ) as Promise<T>;
}

const PRACTICE_ADMIN_ACTIONS_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'practice-admin-actions',
} as const;

const UNIFY_ADMIN_ACTIONS_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'unify-admin-actions',
} as const;

function executePracticeAdminAction<T>(medplum: MedplumClient, input: AdminActionBotInput): Promise<T> {
  return medplum.executeBot(PRACTICE_ADMIN_ACTIONS_BOT_IDENTIFIER, input, ContentType.JSON) as Promise<T>;
}

function executeUnifyAdminAction<T>(medplum: MedplumClient, input: AdminActionBotInput): Promise<T> {
  return medplum.executeBot(UNIFY_ADMIN_ACTIONS_BOT_IDENTIFIER, input, ContentType.JSON) as Promise<T>;
}

export function downloadDocumentAttachment(
  medplum: MedplumClient,
  documentReferenceId: string,
): Promise<{ data: string; contentType: string; title?: string }> {
  return medplum.executeBot(
    { system: 'https://unify.health/bots', value: 'download-document-attachment' },
    { action: 'download-document-attachment', documentReferenceId },
    ContentType.JSON,
  ) as Promise<{ data: string; contentType: string; title?: string }>;
}

// ---------------------------------------------------------------------------
// Typed action wrappers
// ---------------------------------------------------------------------------

/**
 * Map UI User updates to the Bot's edit-members input and execute.
 * Only sends Practitioner-relevant fields — role, permissions, and
 * membership data are NOT sent to the Bot.
 */
export function mapUserUpdatesToEditMembersUpdates(
  userUpdates: Partial<User>,
): EditMembersBotInput['updates'] {
  const updates: EditMembersBotInput['updates'] = {};
  if (userUpdates.name !== undefined) updates.name = userUpdates.name;
  if (userUpdates.email !== undefined) updates.email = userUpdates.email;
  if (userUpdates.phone !== undefined) updates.phone = userUpdates.phone;
  if (userUpdates.bio !== undefined) updates.bio = userUpdates.bio;
  if (userUpdates.aliases !== undefined) updates.aliases = userUpdates.aliases;
  if (userUpdates.clinical !== undefined) updates.clinical = userUpdates.clinical;
  if (userUpdates.preferredNames !== undefined) updates.preferredNames = userUpdates.preferredNames;
  if (userUpdates.locationIds !== undefined) updates.locationIds = userUpdates.locationIds;
  return updates;
}

export async function editMember(
  medplum: MedplumClient,
  userId: string,
  userUpdates: Partial<User>,
): Promise<Practitioner> {
  const input: EditMembersBotInput = {
    action: 'edit-members',
    userId,
    updates: mapUserUpdatesToEditMembersUpdates(userUpdates),
  };
  // admin-actions only edits the caller's own profile; editing another member is a practice-admin action.
  return userId === medplum.getProfile()?.id
    ? executeAdminAction<Practitioner>(medplum, input)
    : executePracticeAdminAction<Practitioner>(medplum, input);
}

/**
 * Directly update a Patient resource for client profile fields (name, email, phone).
 * Clients are stored as Patient resources, not Practitioner, so the edit-members bot path does not apply.
 */
export async function editPatientProfile(
  medplum: MedplumClient,
  patientId: string,
  updates: Pick<Partial<User>, 'name' | 'email' | 'phone'>,
): Promise<Patient> {
  const patient = await medplum.readResource('Patient', patientId);

  if (updates.name !== undefined) {
    const parts = updates.name.trim().split(/\s+/);
    const family = parts.pop() ?? '';
    const given = parts.length > 0 ? parts : undefined;
    const newName: HumanName = { use: 'official', family, given };
    patient.name = patient.name?.length
      ? patient.name.map((n, i) => (i === 0 ? { ...n, ...newName } : n))
      : [newName];
  }

  if (updates.email !== undefined || updates.phone !== undefined) {
    const telecoms: ContactPoint[] = (patient.telecom ?? []).filter(
      (t) =>
        !(updates.email !== undefined && t.system === 'email') &&
        !(updates.phone !== undefined && t.system === 'phone'),
    );
    if (updates.email) {
      telecoms.push({ system: 'email', value: updates.email, use: 'home' });
    }
    if (updates.phone) {
      telecoms.push({ system: 'phone', value: updates.phone, use: 'home' });
    }
    patient.telecom = telecoms;
  }

  return medplum.updateResource(patient);
}

export async function setRole(
  medplum: MedplumClient,
  userId: string,
  role: string,
  permissions?: string,
  additionalRoles?: string[],
): Promise<Practitioner> {
  const updated = await executePracticeAdminAction<Practitioner>(medplum, {
    action: 'set-role',
    userId,
    role,
    permissions,
    // Always forward when the caller knows the full set — `[]` clears extras;
    // omitting the field (undefined) means "keep existing additional roles".
    ...(additionalRoles !== undefined ? { additionalRoles } : {}),
  });
  // Role codes, supervision links, and memberships changed server-side. Cached
  // reads (Practitioner/{id}) survive invalidateSearches, so flush everything.
  medplum.invalidateAll();
  return updated;
}

export async function setPrimaryRole(
  medplum: MedplumClient,
  userId: string,
  primaryRole: string,
): Promise<Practitioner> {
  const updated = await executeAdminAction<Practitioner>(medplum, {
    action: 'set-primary-role',
    userId,
    primaryRole,
  });
  medplum.invalidateAll();
  return updated;
}

export async function resendInvite(
  medplum: MedplumClient,
  input: Omit<ResendInviteBotInput, 'action'>,
): Promise<{ email: string; userSecurityRequestId: string }> {
  return executePracticeAdminAction<{ email: string; userSecurityRequestId: string }>(medplum, {
    ...input,
    action: 'resend-invite',
  });
}

export interface InviteUserResult {
  profileId: string;
  email: string;
  invited: boolean;
}

export async function inviteUser(
  medplum: MedplumClient,
  input: Omit<InviteUserBotInput, 'action'>,
): Promise<InviteUserResult> {
  return executePracticeAdminAction<InviteUserResult>(medplum, {
    ...input,
    action: 'invite-user',
  });
}

export async function createPractice(
  medplum: MedplumClient,
  input: Omit<CreatePracticeBotInput, 'action'>,
): Promise<CreatePracticeResult> {
  return executeUnifyAdminAction<CreatePracticeResult>(medplum, {
    ...input,
    action: 'create-practice',
  });
}

export async function manageEocParty(
  medplum: MedplumClient,
  input: Omit<ManageEocPartyBotInput, 'action'>,
): Promise<ManageEocPartyResult> {
  return executeAdminAction<ManageEocPartyResult>(medplum, {
    ...input,
    action: 'manage-eoc-party',
  });
}

/** Staff manual add-client: pilot tag + practice Organization compartment on Patient. */
export async function ensurePracticeClient(
  medplum: MedplumClient,
  input: Omit<EnsurePracticeClientBotInput, 'action'>,
): Promise<{ patientId: string }> {
  return executeAdminAction<{ patientId: string }>(medplum, {
    ...input,
    action: 'ensure-practice-client',
  });
}

export async function createClientAndEpisode(
  medplum: MedplumClient,
  input: Omit<CreateClientAndEpisodeBotInput, 'action'>,
): Promise<CreateClientAndEpisodeResult> {
  return executeAdminAction<CreateClientAndEpisodeResult>(medplum, {
    action: 'create-client-and-episode',
    ...input,
  });
}

/** Assign, replace, or clear a provider's supervising practitioner. */
export async function updateProviderSupervisor(
  medplum: MedplumClient,
  providerId: string,
  supervisorId: string | null,
): Promise<{ success: true }> {
  return executePracticeAdminAction<{ success: true }>(medplum, {
    action: 'update-provider-supervisor',
    providerId,
    supervisorId,
  });
}

/** Set whether this practitioner's clinical documentation requires cosignature. */
export async function updateRequiresCosignature(
  medplum: MedplumClient,
  providerId: string,
  requiresCosignature: boolean,
): Promise<{ success: true }> {
  return executePracticeAdminAction<{ success: true }>(medplum, {
    action: 'update-requires-cosignature',
    providerId,
    requiresCosignature,
  });
}

/** Replace a supervisor's supervisee list (syncs canonical + reverse extensions). */
export async function updateSupervisorSupervisees(
  medplum: MedplumClient,
  supervisorId: string,
  superviseeIds: string[],
): Promise<{ success: true }> {
  return executePracticeAdminAction<{ success: true }>(medplum, {
    action: 'update-supervisor-supervisees',
    supervisorId,
    superviseeIds,
  });
}

/** Read-only impact analysis for a practitioner role transition. */
export async function analyzeRoleTransition(
  medplum: MedplumClient,
  request: RoleTransitionRequest,
): Promise<RoleTransitionImpactReport> {
  return executePracticeAdminAction<RoleTransitionImpactReport>(medplum, {
    action: 'analyze-role-transition',
    request,
  });
}

/** Validate + execute a practitioner role transition (atomic FHIR transaction). */
export async function executeRoleTransition(
  medplum: MedplumClient,
  request: RoleTransitionRequest,
): Promise<RoleTransitionExecuteResult> {
  const result = await executePracticeAdminAction<RoleTransitionExecuteResult>(medplum, {
    action: 'execute-role-transition',
    request,
  });
  medplum.invalidateAll();
  return result;
}

const NOTIFICATION_EMAIL_BOT = { system: 'https://unify.health/bots', value: 'email-sender' } as const;

/**
 * Send the system status test email to the fixed ops addresses (unify-admin callers only). The
 * `email-sender` bot's `send-test-email` action (ENG-3764 D24) checks the caller and sends; no Task is created.
 */
export async function sendSystemStatusTestEmail(
  medplum: MedplumClient,
): Promise<SendSystemStatusTestEmailResult> {
  return medplum.executeBot(
    NOTIFICATION_EMAIL_BOT,
    { action: 'send-test-email', kind: 'system-status' },
    ContentType.JSON,
  ) as Promise<SendSystemStatusTestEmailResult>;
}

/** Bundled practice-owner swap (unify-admin only). */
export async function swapPracticeOwner(
  medplum: MedplumClient,
  input: Omit<SwapPracticeOwnerBotInput, 'action'>,
): Promise<{ orgId: string; incomingOwnerId: string; outgoingOwnerId: string; operationId: string }> {
  return executeUnifyAdminAction(medplum, {
    action: 'swap-practice-owner',
    ...input,
  });
}

/** Deactivate a practitioner (never deletes User/Practitioner). */
export async function deactivateUser(
  medplum: MedplumClient,
  userId: string,
): Promise<{ deactivated: true; userId: string }> {
  return executePracticeAdminAction(medplum, {
    action: 'deactivate-user',
    userId,
  });
}

/** Reactivate a practitioner with an explicit role (historical access not auto-restored). */
export async function reactivateUser(
  medplum: MedplumClient,
  input: Omit<ReactivateUserBotInput, 'action'>,
): Promise<{ reactivated: true; userId: string }> {
  return executePracticeAdminAction(medplum, {
    action: 'reactivate-user',
    ...input,
  });
}
